import { type ReactNode } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

interface Props {
  nome: string | null | undefined;
  cor?: string | null | undefined;
  size?: number;
}

function LogoTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip.Provider delayDuration={120}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={6}
            className="z-[2200] rounded-md px-2 py-1 shadow-md"
            style={{
              background: 'var(--text)',
              color: 'var(--bg)',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {label}
            <Tooltip.Arrow style={{ fill: 'var(--text)' }} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export function BancoDoBrasilLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Banco do Brasil">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: '#ffed00',
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
        }}
        aria-label="Banco do Brasil"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 22.33 22.33"
          width={size * 0.6}
          height={size * 0.6}
          style={{ display: 'block', pointerEvents: 'none' }}
        >
          <path
            fill="#0059a2"
            fillRule="evenodd"
            d="M0,19.23l2.33-1.55,2.33,1.55-4.65,3.1v-3.1h0ZM22.33,3.1l-2.33,1.55-2.33-1.55L22.33,0v3.1h0ZM22.33,14.89l-11.17,7.44L0,14.89,13.26,6.05l2.33,1.55-10.7,7.13,6.51,4.34,3.02-2.02-2.33-1.55,5.58-3.72,4.65,3.1h0ZM0,7.44L11.17,0l11.17,7.44-13.26,8.84-2.33-1.55,10.7-7.13-6.51-4.34-3.02,2.02,2.33,1.55-5.58,3.72L0,7.44h0Z"
          />
        </svg>
      </div>
    </LogoTooltip>
  );
}

export function SicoobLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Sicoob">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: '#004750',
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
        }}
        aria-label="Sicoob"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 234.17 207.03"
          width={size * 0.62}
          height={size * 0.62}
          style={{ display: 'block', pointerEvents: 'none' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <path
            fill="#bfd730"
            d="M151,93.68l-106.98-.36,61.19,105.93c6.09,9.07,13.6,7.71,13.6,7.71,8.27-1.21,11.66-9.96,11.66-9.96l40.01-69.59h0l-19.49-33.73Z"
          />
          <path
            fill="#76c043"
            d="M63.71,127.21L137.22.12H16.58S7.3-1.25,2.17,5.35c0,0-4.88,5.86,0,15.63l61.54,106.23Z"
          />
          <path
            fill="#04af9d"
            d="M170.49,127.41L96.95.12h120.64s9.28-1.36,14.41,5.23c0,0,4.88,5.86,0,15.63l-61.51,106.43Z"
          />
          <polygon
            fill="#bfd730"
            points="150.72 93.68 170.48 127.42 170.49 127.41 189.81 93.81 150.72 93.68"
          />
        </svg>
      </div>
    </LogoTooltip>
  );
}

export function BancoAmazoniaLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Banco da Amazônia (BASA)">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: '#ffffff',
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
          border: '1px solid var(--border)',
        }}
        aria-label="Banco da Amazônia (BASA)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 36.85 24.76"
          width={size * 0.78}
          height={size * 0.78 * (24.76 / 36.85)}
          style={{ display: 'block', pointerEvents: 'none' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <path
            fill="#fc0"
            d="M28.73,15.14c-2.27-.19-4.29-.97-5.8-1.73-1.15-.58-2-1.14-2.44-1.41l-3.76,5.71c.14.13.21.22.21.22,1.26.81,2.46,1.44,3.59,1.9,3.24,1.33,5.97,1.31,8.2.61,5.58-1.75,8.13-7.76,8.13-7.76-2.76,2.14-5.59,2.67-8.13,2.46h0ZM0,17.64c3.51-3,6.71-3.77,9.37-3.54l3.38-5.1C2.87,7.52,0,17.64,0,17.64h0Z"
          />
          <path
            fill="#00943a"
            d="M20.53,19.84l-1.8,4.83h10.01v-4.22c-2.24.7-4.96.72-8.2-.61h0ZM18.72,0l-5.98,9-3.38,5.1-7.08,10.67h9.81l4.65-7.05,3.76-5.71L27.34,1.59l-4.42,11.82c1.51.76,3.54,1.54,5.8,1.73V0h-10.01Z"
          />
        </svg>
      </div>
    </LogoTooltip>
  );
}

export function TriagemCreditoLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Triagem de Crédito">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: 'linear-gradient(135deg, #14532d 0%, #15803d 65%, #65a30d 100%)',
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
        }}
        aria-label="Triagem de Crédito"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          width={size * 0.72}
          height={size * 0.72}
          style={{ display: 'block', pointerEvents: 'none' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <rect x="8" y="7" width="16" height="20" rx="3" fill="#f7fee7" />
          <rect x="12" y="4.5" width="8" height="4.5" rx="1.8" fill="#facc15" />
          <path
            d="M12 14h8M12 18h8M12 22h5"
            stroke="#166534"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M19.5 22.2l1.8 1.8 3.7-4"
            fill="none"
            stroke="#16a34a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </LogoTooltip>
  );
}

export function BradescoLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Bradesco">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: '#cc092f',
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
        }}
        aria-label="Bradesco"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 82.74 88.04"
          width={size * 0.5}
          height={size * 0.5}
          style={{ display: 'block', pointerEvents: 'none' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <polygon fill="#fff" points="38.27 88.04 32.04 88.04 32.04 56.1 38.27 56.1 38.27 88.04 38.27 88.04" />
          <polygon fill="#fff" points="54.15 88.04 43.94 88.04 43.94 44.2 54.15 44.2 54.15 88.04 54.15 88.04" />
          <path
            fill="#fff"
            d="M26.45,13.68s10.29-10.06,27.63-6.75c0,0,7.63,1.18,13.48,6.38,0,0,.87.82,1.54.15,0,0,.85-.67-.1-1.66,0,0-5.03-6.97-14.95-10.06,0,0-13.64-4.88-27.65,1.72,0,0-8.62,3.57-13.41,13.3,0,0-6.82,2.21-11.93,5.07,0,0-1.51.6-.94,1.77,0,0,.49.84,1.81.22,0,0,4.75-1.64,9.32-2.66,0,0-3.68,9.54.16,21.39,1.71,5.27,4.83,10.64,9.6,14.95,0,0,.78.81,1.62.26,0,0,.73-.53.21-1.4,0,0-3.8-5.72-5.28-13.87-1.23-6.63-.65-15.8,3.96-23.06,0,0,17.55-2.45,32.5,3.18,0,0,25.41,8.29,19.01,23.66,0,0-1.85,5.35-8.87,10.18,0,0-1.17.68-.53,1.66,0,0,.53.76,1.73.27,0,0,10.74-3.49,15.37-12.12,0,0,9.41-14.39-12.77-26.13,0,0-6.09-3.26-13.93-5.01,0,0-14.12-3.49-27.61-1.46"
          />
        </svg>
      </div>
    </LogoTooltip>
  );
}

export function SicrediLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Sicredi">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
          background: '#ffffff',
          border: '1px solid var(--border)',
        }}
        aria-label="Sicredi"
      >
        <img
          src="/logos/sicredi.svg"
          alt=""
          width={size * 0.72}
          height={size * 0.72}
          style={{ display: 'block', pointerEvents: 'none', flexShrink: 0 }}
        />
      </div>
    </LogoTooltip>
  );
}

export function CredisisLogo({ size = 28 }: { size?: number }) {
  return (
    <LogoTooltip label="Credisis">
      <div
        className="flex items-center justify-center"
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.22,
          flexShrink: 0,
          cursor: 'help',
          background: '#ffffff',
          border: '1px solid var(--border)',
        }}
        aria-label="Credisis"
      >
        <img
          src="/logos/credisis.svg"
          alt=""
          width={size * 0.78}
          height={size * 0.78}
          style={{ display: 'block', pointerEvents: 'none', flexShrink: 0 }}
        />
      </div>
    </LogoTooltip>
  );
}

export function InstituicaoLogo({ nome, cor, size = 28 }: Props) {
  if (!nome) return <span className="text-text-muted" style={{ fontSize: '0.82rem' }}>—</span>;

  const normalized = nome.toLowerCase().trim();
  if (normalized === 'banco do brasil' || normalized.startsWith('banco do brasil')) {
    return <BancoDoBrasilLogo size={size} />;
  }
  if (normalized === 'sicoob' || normalized.startsWith('sicoob')) {
    return <SicoobLogo size={size} />;
  }
  if (normalized === 'bradesco' || normalized.startsWith('bradesco') || normalized.includes('banco bradesco')) {
    return <BradescoLogo size={size} />;
  }
  if (normalized === 'sicredi' || normalized.startsWith('sicredi')) {
    return <SicrediLogo size={size} />;
  }
  if (normalized.includes('credisis') || normalized.includes('jicred')) {
    return <CredisisLogo size={size} />;
  }
  if (
    normalized.includes('banco da amazônia') ||
    normalized.includes('banco da amazonia') ||
    normalized === 'basa' ||
    normalized.startsWith('basa')
  ) {
    return <BancoAmazoniaLogo size={size} />;
  }

  return (
    <LogoTooltip label={nome}>
      <div className="flex items-center gap-1.5 cursor-help" style={{ fontSize: '0.82rem' }}>
        {cor && (
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: cor,
              flexShrink: 0,
            }}
          />
        )}
        {nome}
      </div>
    </LogoTooltip>
  );
}
